//
// Copyright 2025 DXOS.org
//

import React, { type ComponentType, type JSX, useCallback } from 'react';

import { URI } from '@dxos/keys';
import { JsonHighlighter, createElement } from '@dxos/react-ui-syntax-highlighter';
import * as Field from '@dxos/react-ui/Field';
import * as Input from '@dxos/react-ui/Input';
import * as SystemButton from '@dxos/react-ui/SystemButton';

export type ObjectViewerProps = {
  object: any;
  /**
   * Renders a copyable link to the object.
   * Prefer to use the DXN from the object.
   */
  id?: string;
  onNavigate?: (dxn: URI.URI) => void;
};

/**
 * Renders a JSON object with navigatable DXN links.
 */
export const ObjectViewer = ({ object, id, onNavigate }: ObjectViewerProps) => {
  const rowRenderer = ({
    rows,
    stylesheet,
    useInlineStyles,
  }: {
    rows: rendererNode[];
    stylesheet: any;
    useInlineStyles: any;
  }) => {
    /**
     * Changes the "dxn:..." span to an anchor tag that navigates to the object.
     */
    const addDxnLinks = (node: rendererNode) => {
      if (isDxnSpanNode(node)) {
        node.tagName = 'a';
        node.properties ??= { className: [] };
        node.properties.className.push('underline', 'cursor-pointer');
        node.properties.onClick = () => {
          onNavigate?.(URI.make((node.children![0].value as string).slice(1, -1)));
        };
      } else {
        node.children?.forEach(addDxnLinks);
      }
    };

    rows.forEach(addDxnLinks);

    return rows.map((row, index) => {
      return createElement({
        node: row,
        stylesheet,
        style: {},
        useInlineStyles,
        key: index,
      });
    });
  };

  const handleCopy = useCallback(() => JSON.stringify(object, null, 2), [object]);

  return (
    <>
      {id && (
        <div className='flex flex-col'>
          <Field.Root>
            <div className='flex flex-col gap-1'>
              <div className='flex gap-1'>
                <Input.Root disabled value={id} />
                <SystemButton.Clipboard iconOnly value={id} />
                <SystemButton.Clipboard label='Copy JSON' onCopy={handleCopy} />
              </div>
            </div>
          </Field.Root>
        </div>
      )}
      <JsonHighlighter data={object} classNames='text-sm' renderer={rowRenderer} />
    </>
  );
};

interface rendererNode {
  type: 'element' | 'text';
  value?: string | number | undefined;
  tagName?: keyof JSX.IntrinsicElements | ComponentType<any> | undefined;
  properties?: { className: any[]; [key: string]: any };
  children?: rendererNode[];
}

const isDxnSpanNode = (node: rendererNode) => {
  return (
    node.type === 'element' &&
    node.tagName === 'span' &&
    node.children?.length === 1 &&
    node.children[0].type === 'text' &&
    typeof node.children[0].value === 'string' &&
    node.children[0].value.match(/^"(dxn:[^"]+)"$/)
  );
};
